import api from "@/utils/axios";
import type {
  TranslationEntryResponse,
  TranslationOverridesResponse,
  UpdateTranslationRequest,
} from "@/types/translation.types";

const OVERRIDES_CACHE_TTL_MS = 30_000;
const TRANSLATION_OVERRIDES_UPDATED_EVENT = "translations-overrides-updated";
const TRANSLATION_OVERRIDES_UPDATED_STORAGE_KEY =
  "translations-overrides-updated-at";

type OverridesCacheEntry = {
  fetchedAt: number;
  data: TranslationOverridesResponse;
};

type GetOverridesOptions = {
  force?: boolean;
};

const overridesCache = new Map<string, OverridesCacheEntry>();
const inFlightOverridesRequests = new Map<
  string,
  Promise<TranslationOverridesResponse>
>();

const normalizeLanguageCode = (languageCode: string) =>
  languageCode.trim().toLowerCase();

const buildBroadcastMarker = (languageCode?: string) =>
  `${Date.now()}:${languageCode ?? "all"}`;

/**
 * API client for translation overrides.
 */
export const translationService = {
  /**
   * Loads translation overrides for a specific language code.
   */
  async getOverrides(
    languageCode: string,
    options: GetOverridesOptions = {}
  ): Promise<TranslationOverridesResponse> {
    const normalizedLanguageCode = normalizeLanguageCode(languageCode);
    const force = options.force === true;
    const now = Date.now();

    if (!force) {
      const cached = overridesCache.get(normalizedLanguageCode);
      if (cached && now - cached.fetchedAt <= OVERRIDES_CACHE_TTL_MS) {
        return { ...cached.data };
      }
      const inFlight = inFlightOverridesRequests.get(normalizedLanguageCode);
      if (inFlight) {
        return inFlight;
      }
    }

    const request = api
      .get<TranslationOverridesResponse>("/translations", {
        params: {
          lang: normalizedLanguageCode,
          ...(force ? { _: now } : {}),
        },
      })
      .then((response) => {
        const data = response.data ?? {};
        overridesCache.set(normalizedLanguageCode, {
          fetchedAt: Date.now(),
          data,
        });
        return { ...data };
      })
      .finally(() => {
        inFlightOverridesRequests.delete(normalizedLanguageCode);
      });

    inFlightOverridesRequests.set(normalizedLanguageCode, request);

    return request;
  },

  /**
   * Creates or updates a translation override for the given key.
   */
  async updateOverride(
    languageCode: string,
    translationKey: string,
    value: string
  ): Promise<TranslationEntryResponse> {
    const payload: UpdateTranslationRequest = { value };
    const response = await api.put<TranslationEntryResponse>(
      `/admin/translations/${languageCode}/${translationKey}`,
      payload
    );
    return response.data;
  },

  /**
   * Deletes a translation override for the given key.
   */
  async deleteOverride(languageCode: string, translationKey: string): Promise<void> {
    await api.delete(`/admin/translations/${languageCode}/${translationKey}`);
  },

  invalidateOverrides(languageCode?: string): void {
    if (languageCode) {
      overridesCache.delete(normalizeLanguageCode(languageCode));
      inFlightOverridesRequests.delete(normalizeLanguageCode(languageCode));
      return;
    }

    overridesCache.clear();
    inFlightOverridesRequests.clear();
  },

  notifyOverridesUpdated(languageCode?: string): void {
    if (typeof window === "undefined") {
      return;
    }

    const marker = buildBroadcastMarker(languageCode);
    const detail = { languageCode, marker };
    window.dispatchEvent(
      new CustomEvent(TRANSLATION_OVERRIDES_UPDATED_EVENT, { detail })
    );
    localStorage.setItem(TRANSLATION_OVERRIDES_UPDATED_STORAGE_KEY, marker);
  },

  getOverridesUpdatedEventName(): string {
    return TRANSLATION_OVERRIDES_UPDATED_EVENT;
  },

  getOverridesUpdatedStorageKey(): string {
    return TRANSLATION_OVERRIDES_UPDATED_STORAGE_KEY;
  },
};
